import { Container } from "pixi.js";

/**
 * Thin wrapper over the Pixi "world" Container. Pan = moving the container,
 * zoom = scaling it around a screen anchor. Both are GPU-cheap composites —
 * no content is re-rasterized when the camera moves (spec §6, regla de oro).
 *
 * Convention: `world` (model) coordinates relate to `screen` (CSS px) by
 *   screen = world * zoom + offset
 * where offset is the container's position and zoom its scale.
 */
export class Camera {
  readonly world: Container;
  minZoom = 0.04;
  maxZoom = 12;

  constructor(world: Container) {
    this.world = world;
  }

  get zoom(): number {
    return this.world.scale.x;
  }

  get x(): number {
    return this.world.position.x;
  }

  get y(): number {
    return this.world.position.y;
  }

  set(x: number, y: number, zoom: number): void {
    this.world.position.set(x, y);
    this.world.scale.set(zoom);
  }

  panBy(dxScreen: number, dyScreen: number): void {
    this.world.position.x += dxScreen;
    this.world.position.y += dyScreen;
  }

  /** Zoom by a multiplicative factor, keeping the screen anchor fixed. */
  zoomAt(factor: number, anchorScreenX: number, anchorScreenY: number): void {
    const oldZoom = this.zoom;
    const newZoom = clamp(oldZoom * factor, this.minZoom, this.maxZoom);
    if (newZoom === oldZoom) return;

    // World point currently under the anchor must stay under it after zoom.
    const wx = (anchorScreenX - this.world.position.x) / oldZoom;
    const wy = (anchorScreenY - this.world.position.y) / oldZoom;
    this.world.scale.set(newZoom);
    this.world.position.set(anchorScreenX - wx * newZoom, anchorScreenY - wy * newZoom);
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return {
      x: (sx - this.world.position.x) / this.zoom,
      y: (sy - this.world.position.y) / this.zoom,
    };
  }

  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    return {
      x: wx * this.zoom + this.world.position.x,
      y: wy * this.zoom + this.world.position.y,
    };
  }

  /** Visible world-space rectangle for the given screen size. */
  visibleWorldRect(screenW: number, screenH: number) {
    const tl = this.screenToWorld(0, 0);
    const br = this.screenToWorld(screenW, screenH);
    return { x: tl.x, y: tl.y, width: br.x - tl.x, height: br.y - tl.y };
  }
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
